import {
  grantIndividualNumberIdentification,
  revokeIndividualNumberIdentification,
} from 'admin/actions/customers';
import { getPrivateMoney } from 'admin/actions/private-moneys';
import Button from 'admin/components/atoms/Buttons';
import { createColorCodedOptions } from 'admin/components/atoms/ColorCodedOption';
import ConfirmModal from 'admin/components/atoms/ConfirmModal';
import Select from 'admin/components/atoms/Select';
import {
  IDENTIFICATION_STATUS,
  IDENTIFICATION_STATUS_LABELS,
  // Aliased because this file's component has the same name
  IdentificationStatus as IdentificationStatusValue,
  identificationStatusOf,
} from 'admin/constants/identificationStatus';
import { useAppSelector } from 'admin/hooks/useApp';
import { components } from 'admin/schema/docs/api/admin';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

type IndividualNumberIdentification = components['schemas']['IndividualNumberIdentification'];

type IdentificationStatusOption = {
  label: string;
  value: IdentificationStatusValue;
  bgColor: string;
};

type ApiMessage = {
  text: string;
  isError: boolean;
};

type Props = {
  customerId?: string;
  privateMoneyId?: string;
  identification?: IndividualNumberIdentification;
  setApiMessages: (messages: ApiMessage[]) => void;
  onUpdated?: () => void;
};

const IDENTIFICATION_STATUS_OPTIONS: IdentificationStatusOption[] = [
  {
    label: IDENTIFICATION_STATUS_LABELS[IDENTIFICATION_STATUS.NOT_VERIFIED],
    value: IDENTIFICATION_STATUS.NOT_VERIFIED,
    bgColor: '#d0d0d0',
  },
  {
    label: IDENTIFICATION_STATUS_LABELS[IDENTIFICATION_STATUS.VERIFIED],
    value: IDENTIFICATION_STATUS.VERIFIED,
    bgColor: '#1e6acc',
  },
  {
    label: IDENTIFICATION_STATUS_LABELS[IDENTIFICATION_STATUS.IS_RESIDENT],
    value: IDENTIFICATION_STATUS.IS_RESIDENT,
    bgColor: '#1eac21',
  },
];

const STATUS_TRANSITIONS: Record<IdentificationStatusValue, IdentificationStatusValue[]> = {
  'not-verified': ['verified', 'is-resident'],
  verified: ['not-verified'],
  'is-resident': ['not-verified'],
};

const formatOptionLabel = createColorCodedOptions(IDENTIFICATION_STATUS_OPTIONS);

const optionOf = (status: IdentificationStatusValue) =>
  IDENTIFICATION_STATUS_OPTIONS.find((option) => option.value === status);

const labelOf = (status: IdentificationStatusValue) => optionOf(status)?.label ?? '';

const isRevocation = (status: IdentificationStatusValue) =>
  status === IDENTIFICATION_STATUS.NOT_VERIFIED;

const selectableOptions = (current: IdentificationStatusValue, canGrant: boolean) =>
  IDENTIFICATION_STATUS_OPTIONS.filter(
    (option) =>
      option.value === current ||
      (STATUS_TRANSITIONS[current].includes(option.value) &&
        (canGrant || isRevocation(option.value)))
  );

const IdentificationStatusForm = styled.div`
  display: flex;
  align-items: center;
`;

const SelectWrapper = styled.div`
  ${Select as any} {
    width: 240px;
  }
`;

const ExpiryDate = styled.span`
  ${({ theme: { spacing } }) => css`
    margin-left: ${spacing['16']};
  `}
`;

const ModalWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spacing['32']};
    margin-top: ${spacing['24']};
  `}
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const IdentificationStatus = ({
  customerId,
  privateMoneyId,
  identification,
  setApiMessages,
  onUpdated,
}: Props) => {
  const user = useAppSelector((state) => state.user);
  const [pendingStatus, setPendingStatus] = useState<IdentificationStatusValue | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isItrustConfigured, setIsItrustConfigured] = useState(false);

  const currentStatus = identificationStatusOf(identification);
  const isPermitted =
    !!customerId &&
    !!privateMoneyId &&
    user.isAllowedTo('write', 'individualNumberIdentification', {}, privateMoneyId);

  useEffect(() => {
    if (!isPermitted || !privateMoneyId) {
      setIsItrustConfigured(false);
      return undefined;
    }

    let isCurrent = true;
    getPrivateMoney(privateMoneyId)
      .then((money) => {
        if (isCurrent) setIsItrustConfigured(!!money.itrust_tenant_code);
      })
      .catch(() => {
        if (isCurrent) setIsItrustConfigured(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [isPermitted, privateMoneyId]);

  // DELETE accepts a revocation even on a money whose iTrust config is disabled;
  // only granting requires it
  const canGrant = isPermitted && isItrustConfigured;
  const options = selectableOptions(currentStatus, canGrant);
  const canEdit = isPermitted && options.length > 1;

  const closeModal = () => setPendingStatus(null);

  const onChangeStatus = (option: IdentificationStatusOption | null) => {
    if (option && option.value !== currentStatus) {
      setPendingStatus(option.value);
    }
  };

  const onConfirm = () => {
    if (!customerId || !privateMoneyId || !pendingStatus) return;

    setIsSubmitting(true);

    const request = isRevocation(pendingStatus)
      ? revokeIndividualNumberIdentification(customerId, privateMoneyId)
      : grantIndividualNumberIdentification(customerId, {
          private_money_id: privateMoneyId,
          is_resident: pendingStatus === IDENTIFICATION_STATUS.IS_RESIDENT,
        });

    request
      .then(() => {
        setIsSubmitting(false);
        setPendingStatus(null);
        setApiMessages([{ text: 'マイナンバー認証を変更しました', isError: false }]);
        onUpdated?.();
      })
      .catch((err) =>
        Promise.resolve(err).then((res) => {
          const message =
            typeof res === 'object' && res?.message ? res.message : 'サーバーエラーが発生しました';
          setIsSubmitting(false);
          setPendingStatus(null);
          setApiMessages([{ text: `変更に失敗しました (${message})`, isError: true }]);
        })
      );
  };

  return (
    <IdentificationStatusForm>
      <SelectWrapper>
        <Select
          options={options}
          value={optionOf(pendingStatus ?? currentStatus)}
          isMulti={false}
          isDisabled={!canEdit}
          formatOptionLabel={formatOptionLabel}
          onChange={onChangeStatus}
        />
      </SelectWrapper>
      {identification?.expires_at && (
        <ExpiryDate>
          {/* expires_at survives expiry, so when only the residency judgement is left
              this is the one on-screen signal that 住民認証済 has lapsed */}
          {identification.is_identified ? '有効期限' : '有効期限切れ'}：
          {moment(identification.expires_at).format('YYYY/MM/DD')}
        </ExpiryDate>
      )}
      <ConfirmModal
        isModalOpen={pendingStatus !== null}
        closeModal={closeModal}
        title="マイナンバーカード認証状態を変更しますか？"
        form={
          <ModalWrapper>
            {`${labelOf(currentStatus)} → ${pendingStatus ? labelOf(pendingStatus) : ''}`}
            <ModalButtonGroup>
              <Button variant="neutral" onClick={closeModal}>
                キャンセル
              </Button>
              <Button variant="contained" onClick={onConfirm} disabled={isSubmitting}>
                変更
              </Button>
            </ModalButtonGroup>
          </ModalWrapper>
        }
      />
    </IdentificationStatusForm>
  );
};

export default IdentificationStatus;
